import React from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  VStack,
  Text,
  Box,
  Flex,
  useColorModeValue,
} from "@chakra-ui/react";

interface HowToUseModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const HowToUseModal: React.FC<HowToUseModalProps> = ({ isOpen, onClose }) => {
  const modalBg = useColorModeValue("white", "#151517");
  const borderColor = useColorModeValue("rgba(56, 187, 209, 0.2)", "rgba(56, 187, 209, 0.4)");
  const textColor = useColorModeValue("#575757", "white");
  const labelColor = useColorModeValue("gray.400", "gray.400");

  const steps = [
    {
      title: "Pick A Setup",
      description: "Browse through the available trade setups organized by Risk/Reward Ratio. Each setup card displays the entry price, take profit (TP), stop loss (SL), and expiry timer.",
    },
    {
      title: "Review The Setup",
      description: "Click on any setup card to view detailed information including the signal reasoning, potential profit, and all trade parameters. Review the setup carefully to ensure it aligns with your trading strategy.",
    },
    {
      title: "Click Take Trade & Input The Remaining Trade Setup Options",
      description: "Once you've selected a setup, click the 'Take Trade' button. You'll be prompted to input any remaining trade setup options such as position size, leverage, or other broker-specific parameters.",
    },
    {
      title: "Place Trade",
      description: "After confirming all trade details, the trade will be sent to your broker via API connection and placed automatically. The trade will be executed according to your broker's settings and the setup parameters.",
    },
    {
      title: "Monitor & Track",
      description: "Once placed, the trade will be added to your trade log with an 'open' status. The system will continuously monitor the trade's progress and automatically update it to 'closed' status when it reaches either the take profit or stop loss level. All trade data will be logged and your dashboard will be updated accordingly.",
    },
  ];

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered scrollBehavior="inside">
      <ModalOverlay backdropFilter="blur(10px)" bg="rgba(0, 0, 0, 0.1)" />
      <ModalContent 
        bg={modalBg} 
        borderRadius="16px" 
        maxH="90vh"
        w={{ base: "90%", md: "600px", lg: "800px" }}
        maxW={{ base: "90%", md: "600px", lg: "800px" }}
      >
        <ModalHeader 
          color="#38BBD1" 
          fontFamily="'Urbanist', sans-serif" 
          fontWeight="normal" 
          px={6}
          pt={6}
          pb={4}
        >
          How To Use Trade Setups
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody px={6} pb={6} overflowY="auto" maxH="calc(90vh - 100px)">
          <VStack align="stretch" spacing={6}>
            <Text
              fontSize="sm"
              color={labelColor}
              fontFamily="'IBM Plex Sans', sans-serif"
              textTransform="uppercase"
              letterSpacing="0.05em"
              mb={2}
            >
              Follow these steps to place a trade:
            </Text>

            <VStack align="stretch" spacing={4}>
              {steps.map((step, index) => (
                <Flex key={index} align="flex-start" gap={3}>
                  <Text
                    fontSize="md"
                    fontWeight="bold"
                    color={textColor}
                    fontFamily="'Urbanist', sans-serif"
                    flexShrink={0}
                    minW="24px"
                  >
                    {index + 1}.
                  </Text>
                  <VStack align="stretch" spacing={2} flex={1}>
                    <Text
                      fontSize="sm"
                      color={labelColor}
                      fontFamily="'IBM Plex Sans', sans-serif"
                      textTransform="uppercase"
                      letterSpacing="0.05em"
                    >
                      {step.title}
                    </Text>
                    <Text
                      fontSize="sm"
                      color={textColor}
                      fontFamily="'Urbanist', sans-serif"
                      lineHeight="1.6"
                    >
                      {step.description}
                    </Text>
                  </VStack>
                </Flex>
              ))}
            </VStack>

            <Box
              mt={4}
              p={4}
              borderRadius="md"
              border="1px solid"
              borderColor={borderColor}
              bg={useColorModeValue("rgba(56, 187, 209, 0.05)", "rgba(56, 187, 209, 0.1)")}
            >
              <Text
                fontSize="xs"
                color={labelColor}
                fontFamily="'IBM Plex Sans', sans-serif"
                textTransform="uppercase"
                letterSpacing="0.05em"
                mb={2}
              >
                Note
              </Text>
              <Text
                fontSize="sm"
                color={textColor}
                fontFamily="'Urbanist', sans-serif"
                lineHeight="1.6"
              >
                All trades are automatically monitored and will be closed when they reach either the take profit or stop loss level. Your dashboard and trade log will be updated in real-time as trades progress.
              </Text>
            </Box>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default HowToUseModal;
